/**
 * Document Processing API service for AI-driven document analysis and processing
 */

import { apiGet, apiPost, apiDelete, apiUpload } from './apiClient';

// Types
export interface ProcessedDocument {
  id: string;
  filename: string;
  original_filename: string;
  file_size: number;
  file_type: string;
  upload_date: string;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  processing_progress: number;
  extracted_text?: string;
  classification?: DocumentClassification;
  summary?: string;
  key_insights?: string[];
  compliance_analysis?: ComplianceAnalysis;
  processing_time?: number;
  error_message?: string;
}

export interface DocumentClassification {
  document_type: string;
  confidence: number;
  regulatory_framework: string[];
  jurisdiction: string[];
  language: string;
}

export interface ComplianceAnalysis {
  overall_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  findings: ComplianceFinding[];
  recommendations: string[];
  regulatory_impact: string;
}

export interface ComplianceFinding {
  category: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  references: string[];
}

export interface ProcessingRequest {
  document_id: string;
  processing_type: string[];
  ai_model?: string;
  jurisdiction?: string;
}

export interface BulkProcessingRequest {
  document_ids: string[];
  processing_type: string[];
  ai_model?: string;
  jurisdiction?: string;
}

export interface DocumentFilters {
  file_type?: string[];
  processing_status?: string[];
  classification?: string[];
  jurisdiction?: string[];
  date_range?: {
    start: string;
    end: string;
  };
}

export interface ProcessingStatistics {
  total_documents: number;
  processed_documents: number;
  pending_documents: number;
  failed_documents: number;
  processing_queue_size: number;
  average_processing_time: number;
  success_rate: number;
  supported_formats: string[];
  processing_types: string[];
}

export interface DocumentInsights {
  document_id: string;
  key_themes: Array<{
    theme: string;
    relevance: number;
    mentions: number;
  }>;
  entities: Array<{
    name: string;
    type: string;
    confidence: number;
    positions: number[];
  }>;
  regulatory_references: Array<{
    regulation: string;
    article: string;
    relevance: number;
  }>;
  risk_indicators: Array<{
    indicator: string;
    risk_level: string;
    confidence: number;
  }>;
  compliance_gaps: Array<{
    requirement: string;
    gap_description: string;
    severity: string;
    recommendation: string;
  }>;
}

// Document Processing API Service
class DocumentProcessingApiService {
  private readonly baseUrl = '/ai-ml/document-processing';

  // Document Management
  async getDocuments(filters?: DocumentFilters): Promise<ProcessedDocument[]> {
    return apiGet(`${this.baseUrl}/documents`, {
      params: filters,
    });
  }

  async getDocument(documentId: string): Promise<ProcessedDocument> {
    return apiGet(`${this.baseUrl}/documents/${documentId}`);
  }

  async uploadDocument(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ProcessedDocument> {
    return apiUpload(`${this.baseUrl}/documents/upload`, file, (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    });
  }

  async deleteDocument(documentId: string): Promise<void> {
    return apiDelete(`${this.baseUrl}/documents/${documentId}`);
  }

  // Document Processing
  async processDocument(request: ProcessingRequest): Promise<{ task_id: string }> {
    return apiPost(`${this.baseUrl}/documents/${request.document_id}/process`, {
      processing_type: request.processing_type,
      ai_model: request.ai_model,
      jurisdiction: request.jurisdiction,
    });
  }

  async processBulkDocuments(request: BulkProcessingRequest): Promise<{ task_id: string }> {
    return apiPost(`${this.baseUrl}/documents/process-bulk`, request);
  }

  async getProcessingStatus(taskId: string): Promise<{
    task_id: string;
    status: string;
    progress: number;
    result?: any;
    error?: string;
  }> {
    return apiGet(`${this.baseUrl}/processing/status/${taskId}`);
  }

  // Document Analysis and Insights
  async getDocumentInsights(documentId: string): Promise<DocumentInsights> {
    return apiGet(`${this.baseUrl}/documents/${documentId}/insights`);
  }

  async getDocumentSummary(documentId: string): Promise<{
    document_id: string;
    summary: string;
    key_points: string[];
    word_count: number;
    complexity_score: number;
  }> {
    return apiGet(`${this.baseUrl}/documents/${documentId}/summary`);
  }

  async compareDocuments(documentIds: string[]): Promise<{
    documents: string[];
    similarities: Array<{
      doc1: string;
      doc2: string;
      similarity_score: number;
      common_themes: string[];
    }>;
    differences: Array<{
      document: string;
      unique_themes: string[];
      distinctive_features: string[];
    }>;
  }> {
    return apiPost(`${this.baseUrl}/documents/compare`, {
      document_ids: documentIds,
    });
  }

  // Statistics and Analytics
  async getProcessingStatistics(): Promise<ProcessingStatistics> {
    return apiGet(`${this.baseUrl}/statistics`);
  }

  async getProcessingQueue(): Promise<Array<{
    document_id: string;
    filename: string;
    queue_position: number;
    estimated_completion: string;
    processing_type: string[];
  }>> {
    return apiGet(`${this.baseUrl}/processing/queue`);
  }

  // Search and Discovery
  async searchDocuments(query: string, filters?: DocumentFilters): Promise<{
    query: string;
    total_results: number;
    documents: Array<ProcessedDocument & {
      relevance_score: number;
      matching_snippets: string[];
    }>;
  }> {
    return apiPost(`${this.baseUrl}/documents/search`, {
      query,
      filters,
    });
  }

  async getDocumentsByClassification(
    classificationType: string,
    jurisdiction?: string
  ): Promise<ProcessedDocument[]> {
    return this.getDocuments({
      classification: [classificationType],
      jurisdiction: jurisdiction ? [jurisdiction] : undefined,
    });
  }

  // Utility endpoints
  async getSupportedFileTypes(): Promise<string[]> {
    return apiGet(`${this.baseUrl}/supported-formats`);
  }

  async getProcessingTypes(): Promise<Array<{
    type: string;
    description: string;
    estimated_time: string;
  }>> {
    return apiGet(`${this.baseUrl}/processing-types`);
  }

  async getClassificationTypes(): Promise<Array<{
    type: string;
    description: string;
    regulatory_framework: string[];
  }>> {
    return apiGet(`${this.baseUrl}/classification-types`);
  }

  // Batch operations
  async getDocumentsWithInsights(documentIds: string[]): Promise<Array<{
    document: ProcessedDocument;
    insights: DocumentInsights;
  }>> {
    const results = await Promise.all(
      documentIds.map(async (id) => {
        const [document, insights] = await Promise.all([
          this.getDocument(id),
          this.getDocumentInsights(id),
        ]);
        return { document, insights };
      })
    );
    return results;
  }

  async getDocumentLibrarySummary(): Promise<{
    total_documents: number;
    by_type: Record<string, number>;
    by_status: Record<string, number>;
    by_classification: Record<string, number>;
    recent_uploads: ProcessedDocument[];
    processing_queue_size: number;
  }> {
    const [documents, statistics] = await Promise.all([
      this.getDocuments(),
      this.getProcessingStatistics(),
    ]);

    const recentUploads = documents
      .sort((a, b) => new Date(b.upload_date).getTime() - new Date(a.upload_date).getTime())
      .slice(0, 5);

    return {
      total_documents: statistics.total_documents,
      by_type: documents.reduce((acc, doc) => {
        acc[doc.file_type] = (acc[doc.file_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      by_status: documents.reduce((acc, doc) => {
        acc[doc.processing_status] = (acc[doc.processing_status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      by_classification: documents.reduce((acc, doc) => {
        if (doc.classification) {
          acc[doc.classification.document_type] = (acc[doc.classification.document_type] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>),
      recent_uploads: recentUploads,
      processing_queue_size: statistics.processing_queue_size,
    };
  }
}

// Export singleton instance
export const documentProcessingApi = new DocumentProcessingApiService();
export default documentProcessingApi;